import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Button from '@wix/da-ds/pkg/Buttons/GhostButton';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import WidgetBoxWrapper from '../WidgetBox/_partials/WidgetBoxWrapper';
import WidgetEditControls from '../WidgetBox/_partials/WidgetEditControls';
import WidgetHeaderWide from '../WidgetBox/lego/WidgetHeaderWide';
import WidgetHeaderMobile from '../WidgetBox/lego/WidgetHeaderMobile';
import useGruser from '../../hooks/useGruser';
import { BiData, ProfileLoadMoreClickBiEvent } from '@wix/da-bi/pkg/events';

import s from './WidgetBoxWide.scss';

export interface Props {
  moduleId: number;
  id?: string;
  header: string | React.ReactNode;
  count?: number | string;
  subHeader?: string;
  headerExtraElementsOnMobile?: React.ReactNode;
  isHeaderStickyOnMobile?: boolean;
  hasMore?: boolean;
  fetchMore?: () => void;
  isFetching?: boolean;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
  parentRef?: any;
  biggerHeader?: boolean;
  confirmRemove?: (onConfirm: Function) => void;
  configPopoverComponent?: React.ComponentType<any>;
}

const WidgetBoxWide: React.FC<Props> = ({
  moduleId,
  isEmpty,
  emptyView,
  children,
  id,
  className,
  parentRef,
  header,
  subHeader,
  count,
  isHeaderStickyOnMobile,
  headerExtraElementsOnMobile,
  hasMore,
  isFetching,
  fetchMore,
  biggerHeader = false,
  confirmRemove,
  configPopoverComponent,
}) => {
  const { gruser } = useGruser();
  const isMobile = useContext(MobileContext);
  const isDesktop = !isMobile;
  const { t } = useTranslation();
  if (!gruser) {
    return null;
  }

  if (isEmpty && !emptyView) {
    return null;
  }

  const renderHeaderExtraElements = () => {
    return (
      isDesktop && (
        <WidgetEditControls
          moduleId={moduleId}
          configPopoverComponent={configPopoverComponent}
          confirmRemove={confirmRemove}
        />
      )
    );
  };

  return (
    <ErrorBoundary debugComponent="WidgetBoxWide">
      <WidgetBoxWrapper
        id={id ?? `module-${moduleId}`}
        className={c(s['widget-wrapper'], className)}
        ref={parentRef}
        data-moduleid={moduleId}
        data-userid={gruser.gruserId}
      >
        {isMobile ? (
          <WidgetHeaderMobile
            header={header}
            count={count}
            isSticky={isHeaderStickyOnMobile}
            extras={headerExtraElementsOnMobile}
            className={s['widget-header']}
            isWide
          />
        ) : (
          <WidgetHeaderWide
            header={header}
            subHeader={subHeader}
            extras={renderHeaderExtraElements()}
            bigger={biggerHeader}
          />
        )}
        <>{isEmpty ? emptyView : children}</>
        {!isEmpty && hasMore && (
          <Button
            size="large"
            variant="main"
            disabled={isFetching}
            className={s['view-more-button']}
            onClick={fetchMore}
            biData={BiData<ProfileLoadMoreClickBiEvent>({
              evid: 265, // generic bi event here
              typeid: 4,
              itemid: gruser.gruserId,
            })}
          >
            {t('common.loadMore')}
          </Button>
        )}
      </WidgetBoxWrapper>
    </ErrorBoundary>
  );
};

WidgetBoxWide.displayName = 'WidgetBoxWide';

export default WidgetBoxWide;
